/** Short-lived, session-authorized relay configuration. Never persisted or logged. */
export interface IceLeaseOptions {
  url: string;
  method?: 'GET' | 'POST';
  headers?: Record<string, string>;
  credentials?: RequestCredentials;
  fetch?: typeof fetch;
  onIceServers(servers: RTCIceServer[]): void;
  onStatus?(status: 'ready' | 'stun-only'): void;
  onSessionExpired?(): void;
}

interface Lease { iceServers: RTCIceServer[]; expiresAt: number; refreshAfterMs: number }

function parseLease(raw: unknown, now: number): Lease {
  if (!raw || typeof raw !== 'object') throw new Error('Invalid ICE lease');
  const value = raw as Record<string, unknown>;
  const ttl = typeof value.expiresAt === 'number' ? value.expiresAt - now : NaN;
  if (!Number.isFinite(ttl) || ttl <= 1000 || ttl > 600_000 ||
      typeof value.refreshAfterMs !== 'number' || !Number.isFinite(value.refreshAfterMs) || value.refreshAfterMs <= 0 ||
      !Array.isArray(value.iceServers) || value.iceServers.length === 0 || value.iceServers.length > 8) throw new Error('Invalid ICE lease');
  const iceServers = value.iceServers.map((rawServer: unknown): RTCIceServer => {
    if (!rawServer || typeof rawServer !== 'object') throw new Error('Invalid ICE server');
    const server = rawServer as Record<string, unknown>;
    if (!Array.isArray(server.urls) || server.urls.length === 0 || server.urls.length > 8 ||
        !server.urls.every(url => typeof url === 'string' && url.length <= 2048 && /^turns?:[^\s@]+$/i.test(url)) ||
        typeof server.username !== 'string' || !server.username || server.username.length > 256 ||
        typeof server.credential !== 'string' || !server.credential || server.credential.length > 1024) throw new Error('Invalid ICE server');
    return { urls: [...server.urls] as string[], username: server.username, credential: server.credential };
  });
  return { iceServers, expiresAt: value.expiresAt as number, refreshAfterMs: value.refreshAfterMs };
}

async function boundedJson(response: Response): Promise<unknown> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error('Empty ICE response');
  let text = '';
  let bytes = 0;
  const decoder = new TextDecoder();
  try {
    while (true) {
      const part = await reader.read();
      if (part.done) break;
      bytes += part.value.byteLength;
      if (bytes > 32_768) throw new Error('ICE response exceeds limit');
      text += decoder.decode(part.value, { stream: true });
    }
    return JSON.parse(text + decoder.decode());
  } finally {
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}

/** Refreshes configuration without rebuilding the stream. Expired leases are never reused. */
export function startIceLease(options: IceLeaseOptions): { ready: Promise<void>; stop(): void } {
  let stopped = false;
  let lease: Lease | null = null;
  let refreshTimer: ReturnType<typeof setTimeout> | undefined;
  let expiryTimer: ReturnType<typeof setTimeout> | undefined;
  let request: AbortController | undefined;
  const doFetch = options.fetch ?? globalThis.fetch;
  const stunOnly = () => {
    lease = null;
    options.onIceServers([]);
    options.onStatus?.('stun-only');
  };
  const refresh = async (): Promise<void> => {
    request = new AbortController();
    const current = request;
    const timeout = setTimeout(() => current.abort(), 4000);
    let unauthorized = false;
    try {
      const response = await doFetch(options.url, {
        method: options.method ?? 'GET', headers: options.headers, credentials: options.credentials ?? 'omit',
        cache: 'no-store', redirect: 'error', signal: current.signal,
      });
      if (!response.ok) {
        unauthorized = response.status === 401 || response.status === 403;
        await response.body?.cancel().catch(() => undefined);
        throw new Error('ICE broker refused');
      }
      const next = parseLease(await boundedJson(response), Date.now());
      if (stopped) return;
      lease = next;
      if (expiryTimer) clearTimeout(expiryTimer);
      expiryTimer = setTimeout(() => { if (!stopped) stunOnly(); }, next.expiresAt - Date.now());
      options.onIceServers(next.iceServers);
      options.onStatus?.('ready');
      const ttl = next.expiresAt - Date.now();
      refreshTimer = setTimeout(() => { void refresh(); }, Math.max(1000, Math.min(next.refreshAfterMs, ttl * 0.8, ttl - 1000)));
    } catch {
      if (stopped) return;
      if (unauthorized) {
        if (expiryTimer) clearTimeout(expiryTimer);
        stunOnly();
        options.onSessionExpired?.();
        return;
      }
      if (!lease || lease.expiresAt <= Date.now()) stunOnly();
      refreshTimer = setTimeout(() => { void refresh(); }, lease ? Math.max(1000, Math.min(10_000, (lease.expiresAt - Date.now()) / 2)) : 10_000);
    } finally {
      clearTimeout(timeout);
      if (request === current) request = undefined;
    }
  };
  const ready = refresh();
  return { ready, stop() {
    stopped = true;
    if (refreshTimer) clearTimeout(refreshTimer);
    if (expiryTimer) clearTimeout(expiryTimer);
    request?.abort();
    lease = null;
  } };
}
